import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
    Calendar as CalendarIcon,
    Mail,
    Truck,
    DollarSign,
    LogOut,
    Loader2,
    CalendarClock,
} from "lucide-react";
import { api, setAuthToken } from "@/lib/api";
import AnimatedLogo from "@/components/site/AnimatedLogo";
import AdminAvailability from "@/components/admin/AdminAvailability";
import BookingsTab from "@/components/admin/BookingsTab";
import PaymentsTab from "@/components/admin/PaymentsTab";
import ContactsTab from "@/components/admin/ContactsTab";
import VendorsTab from "@/components/admin/VendorsTab";
import { toast } from "sonner";

const STAT_CARDS = [
    { key: "bookings", label: "Total bookings", icon: CalendarIcon, color: "text-[#F26B1F]" },
    { key: "paid_bookings", label: "Paid bookings", icon: DollarSign, color: "text-emerald-600" },
    { key: "contacts", label: "Contact messages", icon: Mail, color: "text-[#0E2A47]" },
    { key: "vendor_applications", label: "Vendor applications", icon: Truck, color: "text-[#0E2A47]" },
];

export default function AdminDashboard() {
    const nav = useNavigate();
    const [stats, setStats] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const token = localStorage.getItem("atm_admin_token");
        if (!token) {
            nav("/admin/login", { replace: true });
            return;
        }
        loadAll();
    }, [nav]);

    async function loadAll() {
        setLoading(true);
        try {
            const [s, b, c, v, p] = await Promise.all([
                api.get("/admin/stats"),
                api.get("/admin/bookings"),
                api.get("/admin/contact"),
                api.get("/admin/vendors"),
                api.get("/admin/payments"),
            ]);
            setStats(s.data);
            setBookings(b.data);
            setContacts(c.data);
            setVendors(v.data);
            setPayments(p.data);
        } catch (err) {
            if (err?.response?.status === 401) {
                setAuthToken(null);
                nav("/admin/login", { replace: true });
                return;
            }
            toast.error("Failed to load admin data");
        } finally {
            setLoading(false);
        }
    }

    function logout() {
        setAuthToken(null);
        nav("/admin/login", { replace: true });
    }

    return (
        <div className="min-h-screen bg-[#F9F8F6]" data-testid="admin-dashboard">
            <header className="border-b border-[#EDE9DF] bg-white">
                <div className="container-atm flex h-20 items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        <AnimatedLogo size="h-10" to={null} />
                        <div>
                            <p className="font-display text-lg font-semibold text-[#0E2A47]">
                                AllTask Moves
                            </p>
                            <p className="text-xs uppercase tracking-widest text-[#F26B1F]">
                                Admin dashboard
                            </p>
                        </div>
                    </Link>
                    <Button
                        variant="outline"
                        onClick={logout}
                        className="rounded-full"
                        data-testid="admin-logout-btn"
                    >
                        <LogOut size={16} className="mr-2" /> Sign out
                    </Button>
                </div>
            </header>

            <main className="container-atm py-12">
                {loading ? (
                    <div className="flex h-60 items-center justify-center">
                        <Loader2 className="animate-spin text-[#F26B1F]" />
                    </div>
                ) : (
                    <>
                        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                            {STAT_CARDS.map((c) => (
                                <div key={c.key} className="card-soft p-6" data-testid={`stat-${c.key}`}>
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm font-medium text-[#475569]">{c.label}</p>
                                        <c.icon size={18} className={c.color} />
                                    </div>
                                    <p className="mt-3 font-display text-3xl font-semibold">
                                        {stats?.[c.key] ?? 0}
                                    </p>
                                </div>
                            ))}
                            <div className="card-soft p-6 lg:col-span-4">
                                <p className="text-sm font-medium text-[#475569]">
                                    Revenue (paid) — CAD
                                </p>
                                <p
                                    className="mt-2 font-display text-4xl font-semibold text-[#F26B1F]"
                                    data-testid="stat-revenue"
                                >
                                    ${stats?.revenue_cad?.toFixed(2) ?? "0.00"}
                                </p>
                            </div>
                        </div>

                        <Tabs defaultValue="bookings" className="mt-12">
                            <TabsList className="bg-white">
                                <TabsTrigger value="bookings" data-testid="tab-bookings">
                                    Bookings ({bookings.length})
                                </TabsTrigger>
                                <TabsTrigger value="payments" data-testid="tab-payments">
                                    Payments ({payments.length})
                                </TabsTrigger>
                                <TabsTrigger value="contacts" data-testid="tab-contacts">
                                    Contacts ({contacts.length})
                                </TabsTrigger>
                                <TabsTrigger value="vendors" data-testid="tab-vendors">
                                    Vendors ({vendors.length})
                                </TabsTrigger>
                                <TabsTrigger value="availability" data-testid="tab-availability">
                                    <CalendarClock size={14} className="mr-1.5" /> Availability
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="bookings">
                                <BookingsTab bookings={bookings} onChanged={loadAll} />
                            </TabsContent>
                            <TabsContent value="payments">
                                <PaymentsTab payments={payments} />
                            </TabsContent>
                            <TabsContent value="contacts">
                                <ContactsTab contacts={contacts} />
                            </TabsContent>
                            <TabsContent value="vendors">
                                <VendorsTab vendors={vendors} />
                            </TabsContent>
                            <TabsContent value="availability">
                                <div className="mt-6">
                                    <AdminAvailability />
                                </div>
                            </TabsContent>
                        </Tabs>
                    </>
                )}
            </main>
        </div>
    );
}
